
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { createOrder } from "../../actions/orderActions";
import { fetchPriceSummary } from "../../actions/priceActions";
import { useCart } from "../../components/CartContext";
import { useRestaurantSession } from "../../components/RestaurantSessionProvider";
import {
  isSessionExpiredError,
  getSessionExpiredMessage,
} from "../../utils/sessionErrorHandler";

import "./css/OrderScreen.css";

function OrderScreen() {
  const { restaurantId, tableId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { sessionToken } = useRestaurantSession();
  const { cart, cartCount, clearCart } = useCart();

  const {
    loading: priceLoading,
    summary: priceSummary,
    error: priceError,
  } = useSelector((state) => state.price || {});

  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [paymentOptions, setPaymentOptions] = useState(null);
  useEffect(() => {
    let active = true;
    setPaymentOptions(null);
    axios.get(`/api/restaurants/${encodeURIComponent(restaurantId)}/payment-options/`)
      .then(({ data }) => {
        if (!active) return;
        setPaymentOptions(data);
        setPaymentMethod((previous) =>
          data[`allow_${previous.toLowerCase()}_payment`] ? previous :
          data.allow_cash_payment ? "CASH" :
          data.allow_online_payment ? "ONLINE" : "CASH"
        );
      })
      .catch(() => { if (active) setPaymentOptions({allow_cash_payment:false, allow_online_payment:false}); });
    return () => { active = false; };
  }, [restaurantId]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const basePath = `/${restaurantId}/table/${tableId}`;

  useEffect(() => {
    if (!cart?.length) return;

    const items = cart.map((item) => ({
      menu_item_id: item.id,
      quantity: item.quantity,
    }));

    dispatch(fetchPriceSummary(restaurantId, items));
  }, [dispatch, restaurantId, cart]);

  const priceReady =
    !priceLoading &&
    !priceError &&
    !!priceSummary &&
    priceSummary.total != null &&
    Number.isFinite(Number(priceSummary.total));

  const formatPrice = (value) =>
    `₹${Number(value ?? 0).toFixed(2)}`;

  const handleCreateOrder = async () => {
    if (loading) return;

    setError("");

    if (!paymentOptions || !paymentOptions[`allow_${paymentMethod.toLowerCase()}_payment`]) {
      setError("This payment method is not available for this restaurant.");
      return;
    }

    if (!cart?.length) {
      setError("Your cart is empty.");
      return;
    }

    if (!sessionToken) {
      setError(
        "Dining session is not ready. Please scan the table QR code again."
      );
      return;
    }

    if (!priceReady) {
      setError(
        "Final price is not ready. Please wait or try again."
      );
      return;
    }

    const orderData = {
      items: cart.map((item) => ({
        menu_item_id: item.id,
        quantity: item.quantity,
      })),
      session_token: sessionToken,
      payment_method: paymentMethod,
      table_id: Number(tableId),
      notes: notes.trim(),
    };

    setLoading(true);

    try {
      const createdOrder = await dispatch(
        createOrder(restaurantId, orderData)
      );

      if (!createdOrder?.id) {
        throw new Error("Order ID was not returned by the server.");
      }

      const navigationState = {
        orderId: createdOrder.id,
        total: Number(createdOrder.total),
        paymentMethod,
      };

      if (paymentMethod === "CASH") {
        clearCart();

        navigate(`${basePath}/success`, {
          replace: true,
          state: navigationState,
        });

        return;
      }

      navigate(`${basePath}/payment`, {
        replace: true,
        state: navigationState,
      });
    } catch (err) {
      if (isSessionExpiredError(err)) {
        setError(
          "Your table session has ended. Please scan the table QR code again."
        );
      } else {
        const message = getSessionExpiredMessage(err);
        setError(
          typeof message === "string"
            ? message
            : "Unable to create order."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (!cart?.length) {
    return (
      <div className="theme4-order-page">
        <div className="theme4-order-container">
          <button
            type="button"
            className="theme4-order-back"
            onClick={() => navigate(basePath)}
          >
            ← BACK TO MENU
          </button>

          <div className="theme4-order-empty">
            <div className="theme4-order-empty-icon">◇</div>
            <span>YOUR ORDER</span>
            <h1>YOUR CART IS EMPTY</h1>
            <p>Add some dishes before creating your order.</p>

            <button
              type="button"
              className="theme4-order-primary-button"
              onClick={() => navigate(basePath)}
            >
              EXPLORE MENU
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="theme4-order-page">
      <div className="theme4-order-container">
        <button
          type="button"
          className="theme4-order-back"
          onClick={() => navigate(`${basePath}/checkout`)}
        >
          <span>←</span> BACK TO CHECKOUT
        </button>

        <div className="theme4-order-heading">
          <div className="theme4-order-heading-line">
            <span className="theme4-order-eyebrow">
              FINAL STEP
            </span>
            <span className="theme4-order-number">02</span>
          </div>

          <h1>YOUR ORDER</h1>
          <p>
            Complete your order by selecting your preferred
            payment method.
          </p>
        </div>

        <div className="theme4-order-layout">
          <main className="theme4-order-main">
            <section className="theme4-order-section">
              <div className="theme4-order-section-heading">
                <span className="theme4-section-number">01</span>
                <div>
                  <small>PAYMENT</small>
                  <h2>PAYMENT METHOD</h2>
                </div>
              </div>

              <div className="theme4-payment-options">
                <button
                  type="button"
                  className={`theme4-payment-option ${
                    paymentMethod === "CASH"
                      ? "theme4-payment-option-active"
                      : ""
                  }`}
                  disabled={!paymentOptions?.allow_cash_payment}
                  aria-disabled={!paymentOptions?.allow_cash_payment}
                  style={!paymentOptions?.allow_cash_payment ? {opacity:0.4,cursor:"not-allowed"} : undefined}
                  onClick={() => paymentOptions?.allow_cash_payment && setPaymentMethod("CASH")}
                >
                  <span className="theme4-payment-radio">
                    {paymentMethod === "CASH" && <span />}
                  </span>

                  <span className="theme4-payment-icon">₹</span>

                  <span className="theme4-payment-content">
                    <strong>CASH</strong>
                    <small>Pay at the restaurant</small>
                  </span>

                  {paymentMethod === "CASH" && (
                    <span className="theme4-payment-check">✓</span>
                  )}
                </button>

                <button
                  type="button"
                  className={`theme4-payment-option ${
                    paymentOptions?.allow_online_payment && paymentMethod === "ONLINE"
                      ? "theme4-payment-option-active"
                      : ""
                  }`}
                  disabled={!paymentOptions?.allow_online_payment}
                  aria-disabled={!paymentOptions?.allow_online_payment}
                  style={!paymentOptions?.allow_online_payment ? {opacity:0.4,cursor:"not-allowed"} : undefined}
                  onClick={() => paymentOptions?.allow_online_payment && setPaymentMethod("ONLINE")}
                >
                  <span className="theme4-payment-radio">
                    {paymentOptions?.allow_online_payment && paymentMethod === "ONLINE" && <span />}
                  </span>

                  <span className="theme4-payment-icon">◇</span>

                  <span className="theme4-payment-content">
                    <strong>ONLINE PAYMENT</strong>
                    <small>{paymentOptions?.allow_online_payment ? "Pay securely online" : "Online payment unavailable"}</small>
                  </span>

                  {paymentOptions?.allow_online_payment && paymentMethod === "ONLINE" && (
                    <span className="theme4-payment-check">✓</span>
                  )}
                </button>
              </div>
            </section>

            <section className="theme4-order-section">
              <div className="theme4-order-section-heading">
                <span className="theme4-section-number">02</span>
                <div>
                  <small>NOTES</small>
                  <h2>SPECIAL INSTRUCTIONS</h2>
                </div>
              </div>

              <div className="theme4-notes-wrapper">
                <textarea
                  className="theme4-order-notes"
                  value={notes}
                  onChange={(event) =>
                    setNotes(event.target.value)
                  }
                  placeholder="Any special instructions for the kitchen?"
                  maxLength={500}
                />

                <div className="theme4-order-notes-footer">
                  <span>OPTIONAL</span>
                  <span>{notes.length}/500</span>
                </div>
              </div>
            </section>

            {(error || priceError) && (
              <div className="theme4-order-error" role="alert">
                <span>!</span>
                {error ||
                  (typeof priceError === "string"
                    ? priceError
                    : "Unable to calculate final price.")}
              </div>
            )}
          </main>

          <aside className="theme4-order-summary">
            <div className="theme4-order-summary-top">
              <div>
                <span>ORDER SUMMARY</span>
                <h2>YOUR ORDER</h2>
              </div>

              <small>
                {cartCount} {cartCount === 1 ? "ITEM" : "ITEMS"}
              </small>
            </div>

            <div className="theme4-order-mini-items">
              {cart.map((item, index) => (
                <div
                  className="theme4-order-mini-item"
                  key={item.id}
                >
                  <span className="theme4-mini-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div>
                    <strong>{item.name}</strong>
                    <small>× {item.quantity}</small>
                  </div>

                  <span>
                    {formatPrice(
                      Number(item.price) * Number(item.quantity)
                    )}
                  </span>
                </div>
              ))}
            </div>

            <div className="theme4-order-summary-row">
              <span>SUBTOTAL</span>
              <strong>
                {priceReady
                  ? formatPrice(priceSummary.subtotal)
                  : "Calculating..."}
              </strong>
            </div>

            <div className="theme4-order-summary-row">
              <span>TAX</span>
              <strong>
                {priceReady
                  ? formatPrice(priceSummary.tax)
                  : "—"}
              </strong>
            </div>

            <div className="theme4-order-summary-row">
              <span>SERVICE CHARGE</span>
              <strong>
                {priceReady
                  ? formatPrice(priceSummary.service_charge)
                  : "—"}
              </strong>
            </div>

            {priceReady &&
              Number(priceSummary.discount || 0) > 0 && (
                <div className="theme4-order-summary-row">
                  <span>DISCOUNT</span>
                  <strong>
                    -{formatPrice(priceSummary.discount)}
                  </strong>
                </div>
              )}

            <div className="theme4-order-summary-divider" />

            <div className="theme4-order-total">
              <div>
                <span>TOTAL</span>
                <small>INCLUDING ALL CHARGES</small>
              </div>

              <strong>
                {priceReady
                  ? formatPrice(priceSummary.total)
                  : "Calculating..."}
              </strong>
            </div>

            <div className="theme4-selected-payment">
              <span>PAYMENT</span>
              <strong>
                {paymentMethod === "CASH"
                  ? "CASH"
                  : "ONLINE PAYMENT"}
              </strong>
            </div>

            <button
              type="button"
              className="theme4-order-create-button"
              onClick={handleCreateOrder}
              disabled={
                loading ||
                !sessionToken ||
                !priceReady
              }
            >
              {loading ? (
                <>
                  <span className="theme4-order-spinner" />
                  CREATING ORDER...
                </>
              ) : (
                <>
                  PLACE ORDER <span>→</span>
                </>
              )}
            </button>

            <p className="theme4-order-security-text">
              {!sessionToken
                ? "Dining session unavailable. Please scan the QR code again."
                : paymentMethod === "CASH"
                ? "You will pay at the restaurant."
                : "You will continue to secure online payment after the order is created."}
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default OrderScreen;